/**
 * A module for finding ad container HTMLElements,
 * whether they be new and empty, or in need of refreshing
 */

import { getElemWidthHeight } from "../../../utils/js/utils.js";
import { logFeature } from "../../../../scriptsCore/js/logging.js";

const log = (...args) => logFeature("ads", ...args);

/**
 * Search the page for some empty ad-container elements.
 * If the requested number of containers is not immediately found
 * it will wait until they appear in the DOM
 * @param {*} minNumber miminimum number of container elements to return. Used for initial load, otherwise defaults to 1
 * @returns {promise} a promise containing an array of HTMLElements
 */
export function getEmptySlots(minNumber = 1) {
  return new Promise((resolve) => {
    const search = () => {
      return Array.from(document.querySelectorAll(".ad")).filter((elem) => {
        const [width, height] = getElemWidthHeight(elem);
        return (
          !elem.querySelector("[id^='div-gpt-ad-']") && width > 0 && height > 0
        );
      });
    };

    const found = search();
    if (found.length >= minNumber) {
      log(
        `found ${found.length} empty slots on the page ${found[0]} ${found[0].firstChild}`
      );
      resolve(found);
      return;
    }

    const callback = () => {
      const found = search();
      if (found.length >= minNumber) {
        log(`observer found ${found.length} empty slots on the page`);
        resolve(found);
        observer.disconnect();
      }
    };

    const observer = new MutationObserver(callback);
    const targetNode = document.querySelector("body");
    const observerOptions = {
      childList: true,
      subtree: true,
    };
    observer.observe(targetNode, observerOptions);
  });
}

/**
 * Find any slots that have been resized,
 * so we can refresh them.
 * @returns {array} a list of HTMLElements with ads that no longer fit inside
 */
export function getResizedSlots() {
  const containers = [...document.querySelectorAll(".ad")];
  return containers.filter((container) => {
    const [cw, ch] = getElemWidthHeight(container);
    // ad is the widest iframe inside
    const dummy = { width: -1 };
    const adIfrm = [...container.querySelectorAll("iframe")].reduce(
      (acc, ifrm) => {
        return parseInt(ifrm.width, 10) >= parseInt(acc.width, 10) ? ifrm : acc;
      },
      dummy
    );
    if (adIfrm && adIfrm !== dummy) {
      const { width, height } = adIfrm;
      return parseInt(width, 10) > cw || parseInt(height, 10) > ch;
    }
    return false;
  });
}

/**
 * Get all of the ad container elements that currently exist on the page(synchronously)
 * @returns {array} a list of HTMLElements
 */
export function getAllSlots() {
  return Array.from(document.querySelectorAll(".ad")).filter((elem) => {
    const [width, height] = getElemWidthHeight(elem);
    return width > 0 && height > 0;
  });
}
